import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withServiceClient,
  getBaseUrlOverride,
} from './portal-http';

import { trackerClient } from '@terra/shared-rest-clients-tracker';
import type { trackerContract } from '@terra/shared-contracts-tracker';
import { traceClient } from '@terra/shared-rest-clients-trace';
import type { TraceContract } from '@terra/shared-contracts-trace';
import {
  type TraceAgentNameFilter,
  type TraceGroupByField,
  type TraceOrderFieldDto,
  type TracePreviewDto,
} from '@terra/shared-contracts-trace';
import type {
  BlockedReasonType,
  ConfirmedVulnerabilityFindingStateType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  RELATED_RECORDS_LIFECYCLE_STATE,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';

export const portalTracesApi = {
  getToolHarsByToolCallId: ({
    threadId,
    toolCallId,
  }: {
    threadId: string;
    toolCallId: string;
  }) =>
    tenantAwareApiWrapper<typeof trackerContract.getToolHarsByToolCallId>({
      endpointFn: (headers) =>
        trackerClient.getToolHarsByToolCallId({
          params: { threadId, toolCallId },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  getToolHarsByThreadId: (threadId: string) =>
    tenantAwareApiWrapper<typeof trackerContract.getToolHarsByThreadId>({
      endpointFn: (headers) =>
        trackerClient.getToolHarsByThreadId({
          params: { threadId },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  getToolHarPresenceByThread: (threadId: string) =>
    tenantAwareApiWrapper<typeof trackerContract.getToolHarPresenceByThread>({
      endpointFn: (headers) =>
        trackerClient.getToolHarPresenceByThread({
          params: { threadId },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  getToolHarsByExecutionId: (executionId: string) =>
    tenantAwareApiWrapper<typeof trackerContract.getToolHarsByExecutionId>({
      endpointFn: (headers) =>
        trackerClient.getToolHarsByExecutionId({
          params: { executionId },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  getToolHarById: (id: string) =>
    tenantAwareApiWrapper<typeof trackerContract.getToolHarById>({
      endpointFn: (headers) =>
        trackerClient.getToolHarById({
          params: { id },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  // Asset deletion methods

  getTracesByApplication: async ({
    applicationId,
    threadId,
    name,
    agentName,
    status,
    signalStatus,
    blockedReason,
    confirmedVulnerabilityFindingState,
    endpointIds,
    urlPatterns,
    skip,
    limit,
    orderBy,
    startDate,
    lifecycleState,
  }: {
    applicationId: string;
    threadId?: string;
    name?: string;
    agentName?: TraceAgentNameFilter;
    status?: TracePreviewDto['status'] | TracePreviewDto['status'][];
    signalStatus?: SignalStatusType[];
    blockedReason?: BlockedReasonType[];
    confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
    endpointIds?: string[];
    urlPatterns?: string[];
    skip?: number;
    limit?: number;
    orderBy?: TraceOrderFieldDto[];
    startDate?: Date;
    lifecycleState?: LifecycleStateFilterDto;
  }) => {
    const headers = await createApiHeaders();

    return traceClient.getTracesByApplicationId({
      params: { applicationId },
      query: {
        threadId,
        name,
        agentName,
        status,
        signalStatus,
        blockedReason,
        confirmedVulnerabilityFindingState,
        endpointIds,
        urlPatterns,
        skip,
        limit,
        orderBy,
        startDate,
        lifecycleState,
      },
      ...withServiceClient(headers, 'ai'),
    });
  },

  getTracesBySignalId: (signalId: string) =>
    tenantAwareApiWrapper<typeof TraceContract.getTracesBySignalId>({
      endpointFn: (headers) =>
        traceClient.getTracesBySignalId({
          params: { signalId },
          query: { lifecycleState: RELATED_RECORDS_LIFECYCLE_STATE },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  getTracesCountByApplication: async ({
    applicationId,
    ...query
  }: {
    applicationId: string;
    agentName?: TraceAgentNameFilter;
    name?: string;
    signalStatus?: SignalStatusType[];
    blockedReason?: BlockedReasonType[];
    confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
    endpointIds?: string[];
    urlPatterns?: string[];
    status?: TracePreviewDto['status'] | TracePreviewDto['status'][];
    groupByStatus?: boolean;
    groupBy?: TraceGroupByField;
    startDate?: Date;
    lifecycleState?: LifecycleStateFilterDto;
  }) => {
    const headers = await createApiHeaders();

    return traceClient.getTracesCountByApplicationId({
      params: { applicationId },
      query,
      ...withServiceClient(headers, 'ai'),
    });
  },

  getTraceCountsByEndpoint: async ({
    applicationId,
    agentName,
    lifecycleState,
  }: {
    applicationId: string;
    agentName?: string;
    lifecycleState?: LifecycleStateFilterDto;
  }) => {
    const headers = await createApiHeaders();

    return traceClient.getTraceCountsByEndpoint({
      params: { applicationId },
      query: { agentName, lifecycleState },
      ...withServiceClient(headers, 'ai'),
    });
  },

  abortTrace: async ({
    threadId,
    applicationId,
  }: {
    threadId: string;
    applicationId: string;
  }) => {
    try {
      const headers = await createApiHeaders();
      const response = await fetch(`${getBaseUrlOverride('ai')}/chat/abort`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'X-Chat-ID': threadId,
        },
        body: JSON.stringify({ applicationId }),
      });

      if (!response.ok) {
        throw new Error('Failed to abort trace');
      }

      return response.json() as Promise<{
        success: boolean;
        message: string;
        chatId: string;
        applicationId: string;
      }>;
    } catch (error) {
      console.error('Failed to abort trace:', error);

      throw error;
    }
  },

  abortSubagent: async ({
    threadId,
    applicationId,
    surfaceId,
    targetType,
    toolCallId,
  }: {
    threadId: string;
    applicationId?: string;
    surfaceId?: string;
    targetType?: string;
    toolCallId: string;
  }) => {
    try {
      const headers = await createApiHeaders();
      const response = await fetch(
        `${getBaseUrlOverride('chat')}/chat/abort-subagent`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            'X-Chat-ID': threadId,
          },
          body: JSON.stringify({
            ...(applicationId ? { applicationId } : {}),
            ...(surfaceId ? { surfaceId } : {}),
            ...(targetType ? { targetType } : {}),
            toolCallId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to abort subagent');
      }

      return response.json() as Promise<{
        success: boolean;
        message: string;
        chatId: string;
        applicationId: string;
        toolCallId: string;
      }>;
    } catch (error) {
      console.error('Failed to abort subagent:', error);

      throw error;
    }
  },

  abortNeoExecution: async ({
    threadId,
    applicationId,
  }: {
    threadId: string;
    applicationId: string;
  }) => {
    try {
      const headers = await createApiHeaders();
      const response = await fetch(
        `${getBaseUrlOverride('ai')}/agents/neo/abort`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            'X-Thread-ID': threadId,
          },
          body: JSON.stringify({ applicationId }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to abort Neo execution');
      }

      return response.json() as Promise<{
        success: boolean;
        message: string;
        threadId: string;
        applicationId: string;
      }>;
    } catch (error) {
      console.error('Failed to abort Neo execution:', error);

      throw error;
    }
  },

  deleteTrace: ({
    applicationId,
    threadId,
  }: {
    applicationId: string;
    threadId: string;
  }) =>
    tenantAwareApiWrapper<typeof TraceContract.deleteTrace>({
      endpointFn: (headers) =>
        traceClient.deleteTrace({
          params: { applicationId, threadId },
          ...withServiceClient(headers, 'ai'),
        }),
    }),

  deleteTracesBulk: ({
    applicationId,
    threadIds,
  }: {
    applicationId: string;
    threadIds: string[];
  }) =>
    tenantAwareApiWrapper<typeof TraceContract.deleteTracesBulk>({
      endpointFn: (headers) =>
        traceClient.deleteTracesBulk({
          params: { applicationId },
          body: { threadIds },
          ...withServiceClient(headers, 'ai'),
        }),
    }),
};
